import type { PropsWithChildren } from "react"
import React from "react"

import { GeniusChat } from "components/GeniusChat/GeniusChat"
import { BannerContainer } from "components/Layouts/Banner"
import { Smallprint } from "components/Layouts/Smallprint"
import { StickyFooterWrapper } from "components/Layouts/StickyFooter"
import { NotificationsBanner } from "components/NotificationsBanner/NotificationsBanner"
import { useHasCurrentMembership, useLocale } from "hooks"
import { color } from "lib/theme/theme.css"
import { useHasMounted } from "utils/wrappers"
import { CountryRedirectModal } from "components/Layouts/CountryRedirectModal"

import {
  bannerContainerWrapper,
  container,
  pageWrapper,
  smallPrintContainer,
  trustPilotWidgetContainer,
} from "./style.css"
import { TrustPilotReviewWidget } from "./TrustPilotWidgets"
import { useSkinnyBannerData } from "../../Layouts/Banner/getSkinnyBannerData"
import { MemberModals } from "../../../packages/membership/components/MemberModals/MemberModals"

type Props = PropsWithChildren & {
  showTrustPilotReviews?: boolean
  trustPilotBackgroundColor?: string
  disableBanner?: boolean
  showChat?: boolean
  showStickyFooter?: boolean
}

declare global {
  interface Window {
    zE: any
  }
}

export const DefaultLayout: React.FC<Props> = ({
  children,
  showTrustPilotReviews = false,
  trustPilotBackgroundColor = color.backgroundInverse,
  disableBanner = false,
  showChat = false,
  showStickyFooter = true,
}) => {
  const hasMounted = useHasMounted()
  const locale = useLocale()
  const isMember = useHasCurrentMembership()
  const { loading: loadingBannerData, data: skinnyBannerData } =
    useSkinnyBannerData(isMember)

  return (
    <div className={container} data-testid="DefaultLayout">
      {!disableBanner && (
        <div className={bannerContainerWrapper}>
          <BannerContainer
            skinnyBannerData={skinnyBannerData}
            locale={locale}
            loadingBannerData={loadingBannerData}
          />
        </div>
      )}

      <NotificationsBanner />
      <MemberModals />
      <div className={pageWrapper}>{children} </div>
      {showStickyFooter && hasMounted && <StickyFooterWrapper />}

      {locale === "en-GB" && showTrustPilotReviews && (
        <div
          className={trustPilotWidgetContainer}
          data-testid="TrustPilotReviews"
        >
          <TrustPilotReviewWidget
            trustPilotBackgroundColor={trustPilotBackgroundColor}
          />
        </div>
      )}
      {!disableBanner && (
        <div className={smallPrintContainer} data-testid="SmallprintContainer">
          <Smallprint skinnyBannerData={skinnyBannerData} />
        </div>
      )}
      {showChat && <GeniusChat />}
      <CountryRedirectModal />
    </div>
  )
}
